import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../utils/apiConfig';
import './styles/BannerCarousel.css';

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return apiUrl(url);
};

const BannerCarousel = ({ placement = 'schedule' }) => {
  const [banners, setBanners] = useState([]);
  const [intervalMs, setIntervalMs] = useState(5000);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await axios.get(apiUrl('/api/public/banners'), {
          params: { placement },
          timeout: 4000,
        });
        if (cancelled) return;
        setBanners(Array.isArray(data?.banners) ? data.banners : []);
        setIntervalMs(data?.interval_ms || 5000);
        setIndex(0);
      } catch (error) {
        if (!cancelled) setBanners([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [placement]);

  useEffect(() => {
    if (banners.length < 2 || paused) return undefined;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % banners.length);
    }, Math.max(2500, intervalMs));
    return () => clearInterval(timer);
  }, [banners, intervalMs, paused]);

  if (!banners.length) return null;

  const current = banners[index] || banners[0];
  const imageSrc = resolveImageUrl(current.image_url);

  const go = (dir) => {
    setIndex((prev) => (prev + dir + banners.length) % banners.length);
  };

  const content = (
    <>
      <img src={imageSrc} alt={current.title || 'Реклама'} loading="lazy" />
      {current.title ? <span className="banner-carousel__label">{current.title}</span> : null}
    </>
  );

  return (
    <section
      className="banner-carousel"
      aria-label="Реклама"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchX.current = e.changedTouches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = (e.changedTouches[0]?.clientX ?? 0) - touchX.current;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      {current.link_url ? (
        <a
          className="banner-carousel__slide"
          href={current.link_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {content}
        </a>
      ) : (
        <div className="banner-carousel__slide">{content}</div>
      )}

      {banners.length > 1 && (
        <>
          <button
            type="button"
            className="banner-carousel__nav banner-carousel__nav--prev"
            onClick={() => go(-1)}
            aria-label="Предыдущий баннер"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
          <button
            type="button"
            className="banner-carousel__nav banner-carousel__nav--next"
            onClick={() => go(1)}
            aria-label="Следующий баннер"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
          <div className="banner-carousel__dots" role="tablist" aria-label="Слайды">
            {banners.map((b, i) => (
              <button
                key={b.id || i}
                type="button"
                className={`banner-carousel__dot ${i === index ? 'is-active' : ''}`}
                role="tab"
                aria-label={`Баннер ${i + 1}`}
                aria-selected={i === index}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default BannerCarousel;
